/**
 * Forked from `react-measure@v1.47`
 * MIT License, see LICENSE file
 *
 * Fairly extensive changes:
 * - fixed bug with unsafe call to disconnect ResizeObserver
 * - removed `get-node-dimensions` dependency
 * - converted to TypeScript
 * - generally improved code safety, simplified code
 *
 * */

import React, { Children } from 'react';
import ReactDOM from 'react-dom';
import ResizeObserver from 'resize-observer-polyfill';

import { Rect } from '../../../common/maths/geometry/rect/rectTypes';

const EMPTY_DIMENSIONS = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
};

type MeasureProps = {
    whitelist: Array<keyof Rect>;
    blacklist: Array<keyof Rect>;
    shouldMeasure: boolean;
    onMeasure: (dimensions: Rect) => void;
    children: React.ReactNode;
};

type MeasureState = {
    dimensions: Rect;
};

class Measure extends React.Component<MeasureProps, MeasureState> {
    static defaultProps = {
        whitelist: ['width', 'height', 'top', 'right', 'bottom', 'left'],
        blacklist: [],
        shouldMeasure: true,
        onMeasure: (): void => undefined,
    };

    _node: Element | null;
    _propsToMeasure: Array<keyof Rect>;
    _lastDimensions: Rect = EMPTY_DIMENSIONS;
    resizeObserver: ResizeObserver | null = null;

    state: MeasureState = {
        dimensions: EMPTY_DIMENSIONS,
    };

    constructor(props: MeasureProps) {
        super(props);
        this._node = null;
        this._propsToMeasure = this._getPropsToMeasure(props);
    }

    componentDidMount(): void {
        this._setDOMNode();

        if (!this._node) {
            console.warn('Cannot find DOM node in Measure component');
            return;
        }

        // measure on first render
        this.measure();

        // add component to resize observer to detect changes on resize
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this._node);
    }

    componentDidUpdate({ whitelist, blacklist }: MeasureProps): void {
        // we store the properties ourselves so we need to update them if the
        // whitelist or blacklist props have changed
        if (this.props.whitelist !== whitelist || this.props.blacklist !== blacklist) {
            this._propsToMeasure = this._getPropsToMeasure({ whitelist, blacklist });
        }
    }

    componentWillUnmount(): void {
        if (!this._node) return;

        this.resizeObserver?.disconnect();
        this._node = null;
    }

    _setDOMNode(): void {
        const node = ReactDOM.findDOMNode(this);

        if (node) {
            this._node = node as Element;
        } else {
            this._node = null;
        }
    }

    getDimensions(node = this._node): Rect {
        const clientRect = node?.getBoundingClientRect();
        if (!clientRect) return EMPTY_DIMENSIONS;

        return {
            x: clientRect.x,
            y: clientRect.y,
            width: clientRect.width,
            height: clientRect.height,
            top: clientRect.top,
            right: clientRect.right,
            bottom: clientRect.bottom,
            left: clientRect.left,
        };
    }

    measure = (): void => {
        // bail out if we shouldn't measure
        if (!this.props.shouldMeasure) return;

        // if no parent available we need to requery the DOM node
        if (!this._node?.parentNode) {
            this._setDOMNode();
        }

        const dimensions = this.getDimensions(this._node);
        const isChildFunction = typeof this.props.children === 'function';

        // determine if we need to update our callback with new dimensions or not
        this._propsToMeasure.some((prop) => {
            if (dimensions[prop] !== this._lastDimensions[prop]) {
                // update our callback if we've found a dimension that has changed
                this.props.onMeasure(dimensions);

                // update state to send dimensions to child function
                if (isChildFunction && typeof this !== 'undefined') {
                    this.setState({ dimensions });
                }

                // store last dimensions to compare changes
                this._lastDimensions = dimensions;

                // we don't need to look any further, bail out
                return true;
            }
        });
    };

    _getPropsToMeasure({
        whitelist,
        blacklist,
    }: {
        whitelist: MeasureProps['whitelist'];
        blacklist: MeasureProps['blacklist'];
    }): Array<keyof Rect> {
        return whitelist.filter((prop) => blacklist.indexOf(prop) < 0);
    }

    render(): React.ReactNode {
        const { children } = this.props;
        return Children.only(typeof children === 'function' ? children(this.state.dimensions) : children);
    }
}

export default Measure;
